import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { TextField, Card } from '@mui/material';
import Button from 'react-bootstrap/Button';
import { memberHandler } from '../../redux/handlers';
import { createTableColumns } from '../../utils/createTableColumns';
import { useGlobalTable } from '../../hooks/useGlobalTable';
import AllderTable from '../../components/AllderTable';
import { SuccessModal, ConfirmDeleteModal } from './modals';

const TEXTFIELD_STYLES = {
  InputProps: { sx: { borderRadius: '7px' } },
  sx: {
    '.Mui-disabled': {
      background: '#F8F8F9'
    }
  },
  className: 'w-[200px] !ml',
  size: 'small'
};

export function CustomerGroupList() {
  const { businessSlug, id: memberGroupId } = useParams();
  const dispatch = useDispatch();
  const [groupInfo, setGroupInfo] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchGroupInfo = async () => {
      try {
        const result = await dispatch(memberHandler.getMemberGroupById(memberGroupId));
        setGroupInfo(result);
      } catch (error) {
        console.error('Error fetching group info:', error);
      }
    };

    if (memberGroupId) {
      fetchGroupInfo();
    }
  }, [dispatch, memberGroupId]);

  const fetchMemberGroupMembersData = useCallback(
    async params => {
      const queryParams = {
        page: params.page,
        perPage: params.per_page,
        s: params.s,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder
      };
      return dispatch(memberHandler.getMemberGroupMembersPagination(memberGroupId, businessSlug, queryParams));
    },
    [dispatch, memberGroupId, businessSlug]
  );

  const {
    data: membersData,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    refreshData,
    filterParams
  } = useGlobalTable({
    fetchData: fetchMemberGroupMembersData,
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultPageSize: 10
  });

  const handleExport = () => {
    const params = {
      s: filterParams.s
    };
    dispatch(memberHandler.getMemberGroupMembersExcel(memberGroupId, businessSlug, params));
  };

  const handleRemoveMember = async () => {
    try {
      const requestBody = {
        member_id: selectedMember.id
      };
      await dispatch(memberHandler.removeMemberFromGroup(memberGroupId, businessSlug, requestBody));
      setModalConfirmDelete(prev => ({ ...prev, open: false }));
      setSuccessMessage('ลบสมาชิกออกจากกลุ่มสำเร็จ');
      setModalSuccess(prev => ({ ...prev, open: true }));
      refreshData();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const handleDeleteClick = member => {
    setSelectedMember(member);
    setModalConfirmDelete(prev => ({ ...prev, open: true }));
  };

  const calculateTotalValidPoints = memberPointTotal => {
    if (!memberPointTotal || !Array.isArray(memberPointTotal)) {
      return 0;
    }

    const currentDate = new Date();

    return memberPointTotal.reduce((total, pointRecord) => {
      if (pointRecord.expire_date) {
        const expireDate = new Date(pointRecord.expire_date);
        if (currentDate > expireDate) {
          return total;
        }
      }
      return total + (pointRecord.point || 0);
    }, 0);
  };

  const columns = createTableColumns([
    {
      field: 'code',
      headerName: 'รหัสลูกค้า',
      width: 170,
      sortable: true,
      flex: 1,
      renderCell: ({ row }) => (
        <Link
          to={`/${businessSlug}/customer/edit/${row.id}`}
          style={{ color: '#2DC76D', fontWeight: 500, textDecoration: 'none' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.code || ''}
        </Link>
      )
    },
    {
      field: 'customerName',
      headerName: 'ชื่อลูกค้า',
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <div>
          {row.first_name_th || ''} {row.last_name_th || ''}
        </div>
      )
    },
    {
      field: 'phone_number',
      headerName: 'เบอร์โทรศัพท์',
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => <div>{row.phone_number || ''}</div>
    },
    {
      field: 'memberGroup',
      headerName: 'กลุ่มลูกค้า',
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => <div>{row.memberGroup?.name || ''}</div>
    },
    {
      field: 'point',
      headerName: 'คะแนนสะสม',
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => {
        const totalValidPoints = calculateTotalValidPoints(row.memberPointTotal);
        return <div>{totalValidPoints}</div>;
      }
    },
    {
      field: 'actions',
      headerName: 'จัดการ',
      width: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <div>
          <Button
            type="button"
            className="btn-delete-st-3 ml-2"
            onClick={() => handleDeleteClick(row)}
            title="ลบออกจากกลุ่ม"
          />
        </div>
      )
    }
  ]);

  const [modalConfirmDelete, setModalConfirmDelete] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 470,
    height: 320,
    pin: false,
    mode: 'normal',
    modalname: 'confirmDelete'
  });

  const [modalSuccess, setModalSuccess] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 470,
    height: 300,
    pin: false,
    mode: 'normal',
    modalname: 'success'
  });

  const handleCloseSuccess = () => {
    setModalSuccess(prev => ({ ...prev, open: false }));
    setSuccessMessage('');
  };

  return (
    <div>
      <nav aria-label="breadcrumb" className="mt-2">
        <ol className="breadcrumb fs-16 ps-0">
          <li className="breadcrumb-item">
            <Link to={`/${businessSlug}/customer-group`} className="tc-7 text-decoration-none">
              สมาชิก/ลูกค้า
            </Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/${businessSlug}/customer-group`} className="tc-7 text-decoration-none">
              จัดการกลุ่มลูกค้า
            </Link>
          </li>
          <li className="breadcrumb-item active tc-1" aria-current="page">
            {groupInfo?.name || 'รายการสมาชิกในกลุ่ม'}
          </li>
        </ol>
      </nav>

      <div className="fs-22 fw-bold me-auto mt-4 mb-2">{groupInfo?.name || ''}</div>

      <Card className="!rounded-lg mb-4">
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap">
            <div className="d-flex align-items-center mb-2">
              <button className="btn btn-export-st-1" onClick={handleExport}>
                Export
              </button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div className="me-3">
                <TextField
                  {...TEXTFIELD_STYLES}
                  className="w-[250px]"
                  placeholder="ค้นหาลูกค้า"
                  onChange={e => debouncedSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          <AllderTable
            data={membersData}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </Card>

      <ConfirmDeleteModal
        modal={modalConfirmDelete}
        setModal={setModalConfirmDelete}
        onConfirm={handleRemoveMember}
        editData={{ customers: 0 }}
        itemType={`สมาชิก "${selectedMember?.first_name_th} ${selectedMember?.last_name_th}" ออกจากกลุ่ม`}
      />

      <SuccessModal
        modal={modalSuccess}
        setModal={setModalSuccess}
        onHide={handleCloseSuccess}
        textsuccess={successMessage}
      />
    </div>
  );
}
